// Simple IndexedDB-backed offline queue for writes

const DB_NAME = 'astra_offline';
const STORE = 'queue';
const DB_VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result;
    try {
      result = fn(store);
    } catch (e) {
      reject(e);
      return;
    }
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
  });
}

export async function enqueue(item) {
  const record = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    createdAt: new Date().toISOString(),
    tryCount: 0,
    ...item,
  };
  await withStore('readwrite', (store) => store.add(record));
  return record.id;
}

export async function getAll() {
  return withStore('readonly', (store) => store.getAll());
}

export async function remove(id) {
  return withStore('readwrite', (store) => store.delete(id));
}

// A 4xx (other than expired-auth 401 and transient 429) is a permanent server
// rejection — retrying the identical request will never succeed, so the item
// must be dropped or the queue retries it forever on every reconnect.
function isPermanentRejection(status) {
  return status >= 400 && status < 500 && status !== 401 && status !== 429;
}

// Flush logic for known item types
export async function flush({ supabase, accessToken }) {
  const items = await getAll();
  for (const item of items) {
    try {
      if (item.type === 'progress_upsert') {
        const { data, options } = item.payload || {};
        if (!data) { await remove(item.id); continue; }
        await supabase
          .from('user_progress')
          .upsert(data, options || { onConflict: 'user_id,page_id', returning: 'minimal' });
        await remove(item.id);
      } else if (item.type === 'quiz_response') {
        const { row } = item.payload || {};
        if (!row) { await remove(item.id); continue; }
        const { error } = await supabase
          .from('quiz_responses')
          .upsert(row, {
            onConflict: 'user_id,page_id,attempt_number',
            ignoreDuplicates: true,
          });
        if (!error) {
          await remove(item.id);
        }
      } else if (item.type === 'scenario_attempt') {
        const { scenarioId, courseId, path } = item.payload || {};
        const headers = { 'Content-Type': 'application/json' };
        if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
        const res = await fetch(`/api/scenarios/${scenarioId}/attempts`, {
          method: 'POST', headers, body: JSON.stringify({ path, courseId })
        });
        if (res.ok || isPermanentRejection(res.status)) {
          await remove(item.id);
        }
      } else if (item.type === 'activity_start_attempt') {
        const { activityId, attempt_data } = item.payload || {};
        if (!activityId || !attempt_data) { await remove(item.id); continue; }
        const headers = { 'Content-Type': 'application/json' };
        if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
        const res = await fetch(`/api/activities/${activityId}/attempts`, {
          method: 'POST', headers, body: JSON.stringify({ attempt_data })
        });
        if (res.ok) {
          const data = await res.json();
          // Store the attempt ID for completion later if needed
          if (item.tempAttemptId && data.attempt?.id) {
            localStorage.setItem(`astra.temp.attempt.${item.tempAttemptId}`, data.attempt.id);
          }
          await remove(item.id);
        } else if (isPermanentRejection(res.status)) {
          await remove(item.id);
        }
      } else if (item.type === 'activity_complete_attempt') {
        const { activityId, completionData } = item.payload || {};
        let { attemptId } = item.payload || {};
        if (!activityId || !attemptId || !completionData) { await remove(item.id); continue; }
        // A completion queued against a temp_ attempt id can only succeed
        // after the corresponding start-attempt has synced and left a real id
        // mapping — remap now, or keep the item queued until the mapping exists.
        if (String(attemptId).startsWith('temp_')) {
          const realAttemptId = localStorage.getItem(`astra.temp.attempt.${attemptId}`);
          if (!realAttemptId) continue;
          attemptId = realAttemptId;
        }
        const headers = { 'Content-Type': 'application/json' };
        if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
        const res = await fetch(`/api/activities/${activityId}/attempts/${attemptId}/complete`, {
          method: 'POST', headers, body: JSON.stringify(completionData)
        });
        if (res.ok || isPermanentRejection(res.status)) {
          await remove(item.id);
        }
      } else if (item.type === 'page_completion_xp') {
        const { pageId } = item.payload || {};
        if (!pageId) { await remove(item.id); continue; }
        const headers = { 'Content-Type': 'application/json' };
        if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
        const res = await fetch('/api/gamification/xp/page-completion', {
          method: 'POST', headers, body: JSON.stringify({ pageId })
        });
        if (res.ok || isPermanentRejection(res.status)) {
          await remove(item.id);
        }
      }
    } catch {
      // keep in queue; will retry next time
    }
  }
}

export function initOnlineSync({ supabase, accessTokenProvider }) {
  window.addEventListener('online', async () => {
    try {
      const token = accessTokenProvider ? await accessTokenProvider() : undefined;
      await flush({ supabase, accessToken: token });
    } catch {}
  });
}
